import axios from 'axios';
import { api } from '../../services/api';
import standardPoolApi from '../../services/standardPoolApi';
import { API_ORIGIN } from '../../config';
import type { Pool } from '../../types/domain';
import { isNormalOperatingPoolStatus } from '../../utils/poolUtils';
import {
  isCctvEligibleForMonitoring,
  isCentralMonitoringCctvPurpose,
  type PoolMonitoringCctv,
} from './monitoringPopupHtml';

const API_URL = API_ORIGIN;

export function normalizeCctvListFromApiPayload(payload: unknown): PoolMonitoringCctv[] {
  if (Array.isArray(payload)) return payload as PoolMonitoringCctv[];
  if (payload && typeof payload === 'object') {
    const obj = payload as { data?: unknown; items?: unknown };
    if (Array.isArray(obj.data)) return obj.data as PoolMonitoringCctv[];
    if (Array.isArray(obj.items)) return obj.items as PoolMonitoringCctv[];
    if (obj.data && typeof obj.data === 'object') return [obj.data as PoolMonitoringCctv];
  }
  return [];
}

export function filterCentralMonitoringCctvs(
  cctvs: PoolMonitoringCctv[]
): PoolMonitoringCctv[] {
  return cctvs.filter(
    (cctv) => isCctvEligibleForMonitoring(cctv) && isCentralMonitoringCctvPurpose(cctv)
  );
}

export async function fetchMonitoringPlaybackRemote(): Promise<boolean> {
  try {
    const res = await axios.get(`${API_URL}/api/gateway/playback-config`);
    return Boolean(res.data?.use_remote_playback);
  } catch {
    return false;
  }
}

export async function fetchMonitoringPoolsList(): Promise<Pool[]> {
  const response = await standardPoolApi.getAllPools();
  if (!response.success) return [];

  const responseData = response.data as Pool[] | { data?: Pool[]; pools?: Pool[] };
  let list: Pool[] = [];
  if (Array.isArray(responseData)) list = responseData;
  else if (responseData?.data && Array.isArray(responseData.data)) list = responseData.data;
  else if (responseData?.pools && Array.isArray(responseData.pools)) list = responseData.pools;

  return list.filter((p) =>
    isNormalOperatingPoolStatus(
      p.status,
      (p as Pool & { is_active?: boolean }).is_active
    )
  );
}

export async function fetchPoolCctvsForMonitoring(
  poolId: string | number
): Promise<PoolMonitoringCctv[]> {
  // named `api` = axios 인스턴스 → response.data 가 본문
  const response = await api.get(`/api/pools/${poolId}/cctvs`);
  const raw = normalizeCctvListFromApiPayload(response.data);
  return filterCentralMonitoringCctvs(raw);
}

export async function loadPoolByCode(poolCode: string): Promise<Pool | null> {
  const poolRes = await standardPoolApi.getPoolByCode(poolCode);
  if (!poolRes.success || !poolRes.data) return null;
  const data = poolRes.data as Pool | { data?: Pool };
  if (data && typeof data === 'object' && 'data' in data && data.data) {
    return data.data as Pool;
  }
  return poolRes.data as Pool;
}
